import { AxiosError } from "axios";
import { getCdnClient } from "../components/shared/services/api/cdnClient";
import { Restaurant } from "../components/shared/types/restaurant.types";
import { config } from "../config";
import { Coordinates } from "../types/user";
import haversineDistance from "haversine-distance";
import { CDNGridData } from "../components/shared/types/grid";

/**
 * Get sorted grid IDs within 25km radius of given location
 *
 * Config requirements for calculateGrids:
 * gridResolution: 0.01 (~1.11km at equator)
 * boundaryThreshold: 0.2 (20% of grid size)
 *
 * Grid Coverage Calculation:
 * - 25km radius requires approximately 23 grid cells in each direction
 * - At gridResolution 0.01, each grid is ~1.11km at equator
 * - We generate a square grid that encompasses the 25km radius circle
 * - Then filter grids by actual distance using haversine
 *
 * Performance:
 * - Generation: ~5-10ms
 * - Distance calculations: ~15-20ms
 * - Sorting: ~5-10ms
 * Total: ~25-40ms on mid-range mobile devices (4 core)
 */
export function getProximityGridIds(
  location: Coordinates,
  maxDistanceKm: number = 10
): string[] {
  const KM_PER_DEGREE = 111; // Approximate kilometers per degree (for latitude)
  const gridResolution = config.grid.resolution; // in degrees eg  0.05
  const gridCellSizeKm = gridResolution * KM_PER_DEGREE; // converts grid resolution to km (e.g., 0.05 * 111 ≈ 5.55km)
  // Calculate the number of grid cells needed to cover maxDistanceKm
  // 0.05 degrees ≈ 1.11km at equator, so we divide distance by 1.11
  const gridCellsNeeded = Math.ceil(maxDistanceKm / gridCellSizeKm);
  console.log(
    "gridCellsNeeded: ",
    gridCellsNeeded,
    ", gridCellSizeKm: ",
    gridCellSizeKm,
    "maxDistanceKm: ",
    maxDistanceKm
  );

  // Generate all potential grid IDs in a square covering the radius
  const potentialGrids: string[] = [];
  const baseLat =
    Math.round(location.latitude / gridResolution) * gridResolution;
  const baseLone = Math.round(72.85 / gridResolution) * gridResolution;
  console.log("baseLat: ", baseLat, ", baseLon: ", baseLone);

  for (let i = -gridCellsNeeded; i <= gridCellsNeeded; i++) {
    for (let j = -gridCellsNeeded; j <= gridCellsNeeded; j++) {
      const lat = baseLat + i * gridResolution;
      const lon = baseLone + j * gridResolution;

      // Format to two decimals
      const gridId = `${lat.toFixed(2)}_${lon.toFixed(2)}`;

      // Calculate distance to the center of this grid
      const distance =
        haversineDistance(
          { lat: location.latitude, lon: location.longitude },
          { lat, lon }
        ) / 1000; // Convert meters to kilometers

      // Only include grids within maxDistanceKm
      if (distance <= maxDistanceKm) {
        potentialGrids.push(gridId);
      }
      console.log("distance:", distance);
      console.log(", potentialGrids: ", potentialGrids);
    }
  }

  // Sort grids by distance from the location
  const griIds = potentialGrids.sort((gridA, gridB) => {
    const [latA, lonA] = gridA.split("_").map(Number);
    const [latB, lonB] = gridB.split("_").map(Number);

    const distA = haversineDistance(
      { lat: location.latitude, lon: location.longitude },
      { lat: latA, lon: lonA }
    );
    const distB = haversineDistance(
      { lat: location.latitude, lon: location.longitude },
      { lat: latB, lon: lonB }
    );

    return distA - distB;
  });

  return griIds;
}

export async function fetchGridData(
  gridId: string
): Promise<CDNGridData | null> {
  console.log("[fetchGridData] Fetching data for grid", { gridId });
  const gridFileUrl = `/restgrid/grids/${gridId}.json`;

  try {
    const response = await getCdnClient(gridFileUrl, {
      useCache: false,
    });

    console.log("[fetchGridData] Successfully fetched grid data", {
      gridId,
      restaurantsCount: response?.length || 0,
    });

    return response;
  } catch (error) {
    const axiosError = error as AxiosError;

    // If the grid data is not found (404), return null instead of throwing
    if (axiosError.response?.status === 404) {
      console.log("[fetchGridData] Grid data not found", {
        gridId,
        status: 404,
      });
      return null;
    }

    // For other errors, log and re-throw
    console.error("[fetchGridData] Failed to fetch grid data", {
      gridId,
      status: axiosError.response?.status,
      error: axiosError.message,
    });
    throw error;
  }
}
